import {
  useMutation,
  useQueries,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useServices } from '@/contexts/app-services';
import { prepareGoalUpdate } from '@/entities/goal/operations';
import type { Goal, GoalUpdateInput } from '@/entities/goal/types';
import { logError } from '@/services/logger';

export const goalKeys = {
  byId: (id: string) => ['goals', id] as const,
};

// Temporary debug context for https://covoy-0z.sentry.io/issues/7302869130/
// Remove once the stale planned_goals root cause is confirmed and fixed.
interface GoalsByIdsDebugContext {
  source?: string;
  planId?: string;
}

export function useGoalsByIds(
  goalIds: string[],
  debugContext?: GoalsByIdsDebugContext,
) {
  const { api } = useServices();

  const queries = useQueries({
    queries: goalIds.map((id) => {
      return {
        queryKey: goalKeys.byId(id),
        queryFn: async () => {
          const goal = await api.goals.readGoal(id);
          if (!goal) {
            logError(
              new Error(`Goal not found: ${id}`),
              'Goal lookup returned no rows',
              {
                goalId: id,
                requestedGoalIds: goalIds,
                ...debugContext,
              },
            );
            return null;
          }
          return goal;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        // This prevents loading flashes by keeping previous data during refetches
        placeholderData: (previousData: Goal | null | undefined) =>
          previousData,
      };
    }),
  });

  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error;

  const queryGoals = goalIds.map((_, index) => queries[index]?.data);

  const hasUndefinedGoals = queryGoals.some((g) => g === undefined);
  const hasPendingQueries = queries.some((q) => q.isPending);

  const goals =
    hasUndefinedGoals && hasPendingQueries
      ? undefined
      : queryGoals.filter((g): g is Goal => g !== undefined && g !== null);

  return {
    goals,
    isError,
    error,
  };
}

async function patchGoalInCache(
  queryClient: QueryClient,
  goalId: string,
  updates: Partial<Goal>,
): Promise<Goal> {
  await queryClient.cancelQueries({ queryKey: goalKeys.byId(goalId) });

  const previousGoal = queryClient.getQueryData<Goal>(goalKeys.byId(goalId));

  if (!previousGoal) {
    throw new Error('Goal no longer exists');
  }

  queryClient.setQueryData(goalKeys.byId(goalId), {
    ...previousGoal,
    ...updates,
  });

  return previousGoal;
}

const POSTGREST_NO_ROWS = 'PGRST116';

function isDeletedGoalError(error: {
  code?: string;
  details?: string;
}): boolean {
  return (
    error.code === POSTGREST_NO_ROWS &&
    error.details === 'The result contains 0 rows'
  );
}

function fireGoalAnalytics(
  analytics: ReturnType<typeof useServices>['analytics'],
  params: {
    updates: GoalUpdateInput;
    previousStatus: string;
    goal: Goal;
    period: string;
    startAtLocal: string;
    position: number;
  },
): void {
  const { updates, previousStatus, goal, period, startAtLocal, position } =
    params;

  if (updates.status === 'done' && previousStatus !== 'done') {
    if (goal.doneAtLocal) {
      analytics.trackCompletedGoal({
        goalId: goal.id,
        period,
        startAtLocal,
        position,
        doneAtLocal: goal.doneAtLocal,
      });
    }
  } else if (
    updates.status !== undefined &&
    updates.status !== 'done' &&
    previousStatus === 'done'
  ) {
    analytics.trackUncompletedGoal({
      goalId: goal.id,
      period,
      startAtLocal,
      position,
    });
  } else {
    analytics.trackUpdatedGoal({
      goalId: goal.id,
      period,
      startAtLocal,
      position,
    });
  }
}

export function useUpdateGoal() {
  const queryClient = useQueryClient();
  const { api, analytics } = useServices();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      updates: GoalUpdateInput;
      period: string;
      startAtLocal: string;
      position: number;
      previousStatus: string;
    }): Promise<Goal> => {
      const { id, updates } = params;
      const validatedUpdate = prepareGoalUpdate(updates);
      return await api.goals.updateGoal(id, validatedUpdate);
    },
    onMutate: async (params) => {
      const { id, updates } = params;
      const previousGoal = await patchGoalInCache(
        queryClient,
        id,
        prepareGoalUpdate(updates),
      );
      return { previousGoal, id };
    },
    onSuccess: (goal, params) => {
      const { updates, period, startAtLocal, position, previousStatus } =
        params;
      fireGoalAnalytics(analytics, {
        updates,
        previousStatus,
        goal,
        period,
        startAtLocal,
        position,
      });
    },
    onError: (error: { code: string; details: string }, _, context) => {
      if (isDeletedGoalError(error)) {
        return;
      }

      logError(error, 'Caught error in mutation function');

      if (context?.previousGoal && context.id) {
        queryClient.setQueryData(
          goalKeys.byId(context.id),
          context.previousGoal,
        );
      }
    },
  });
}

interface DebouncedMutationParams {
  title: string;
  seq: number;
}

const TITLE_DEBOUNCE_MS = 400;

export function useDebouncedGoalUpdate(
  goalId: string,
  options: { period: string; startAtLocal: string; position: number },
): { updateTitle: (title: string) => void } {
  const queryClient = useQueryClient();
  const { api, analytics } = useServices();
  const lastConfirmedTitleRef = useRef(
    queryClient.getQueryData<Goal>(goalKeys.byId(goalId))?.title ?? '',
  );
  const lastAppliedSeqRef = useRef(0);
  const mutationSeqRef = useRef(0);
  const pendingTitleRef = useRef<string | null>(null);

  const { mutate } = useMutation({
    mutationFn: async (params: DebouncedMutationParams): Promise<Goal> => {
      const validatedUpdate = prepareGoalUpdate({ title: params.title });
      return await api.goals.updateGoal(goalId, validatedUpdate);
    },
    onSuccess: (goal, params) => {
      if (params.seq >= lastAppliedSeqRef.current) {
        lastAppliedSeqRef.current = params.seq;
        lastConfirmedTitleRef.current = params.title;
      }
      fireGoalAnalytics(analytics, {
        updates: { title: params.title },
        previousStatus: goal.status,
        goal,
        period: options.period,
        startAtLocal: options.startAtLocal,
        position: options.position,
      });
    },
    onError: (error: { code?: string; details?: string }, params) => {
      if (isDeletedGoalError(error)) {
        return;
      }

      logError(error, 'Caught error in debounced goal update');

      if (params.seq >= lastAppliedSeqRef.current) {
        const currentGoal = queryClient.getQueryData<Goal>(
          goalKeys.byId(goalId),
        );
        if (currentGoal) {
          queryClient.setQueryData(goalKeys.byId(goalId), {
            ...currentGoal,
            title: lastConfirmedTitleRef.current,
          });
        }
      }
    },
  });

  const debouncedMutate = useDebouncedCallback((title: string) => {
    pendingTitleRef.current = null;
    mutationSeqRef.current += 1;
    mutate({ title, seq: mutationSeqRef.current });
  }, TITLE_DEBOUNCE_MS);

  useEffect(() => {
    function flushPendingTitleUpdate(): void {
      if (pendingTitleRef.current === null) {
        return;
      }

      const title = pendingTitleRef.current;
      pendingTitleRef.current = null;
      debouncedMutate.cancel();
      mutationSeqRef.current += 1;
      mutate({ title, seq: mutationSeqRef.current });
    }

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        flushPendingTitleUpdate();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', flushPendingTitleUpdate);

    return () => {
      flushPendingTitleUpdate();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', flushPendingTitleUpdate);
    };
  }, [debouncedMutate, mutate]);

  function updateTitle(title: string): void {
    const currentGoal = queryClient.getQueryData<Goal>(goalKeys.byId(goalId));
    if (!currentGoal) {
      throw new Error(`Goal ${goalId} not found in cache`);
    }
    queryClient.setQueryData(goalKeys.byId(goalId), { ...currentGoal, title });
    pendingTitleRef.current = title;
    debouncedMutate(title);
  }

  return { updateTitle };
}
